"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, FileCheck2, ArrowRight, Sparkles, SlidersHorizontal, CircleCheck as CheckCircle2, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
export default function FamilyHero() {
  const shouldReduceMotion = useReducedMotion();
  const keySpecifications = [{
    id: 1,
    title: "Pharmacopoeia Compliance",
    desc: "Meets EP 2.9.36, USP <1174>, and USP <1217> standards"
  }, {
    id: 2,
    title: "Data Integrity Support",
    desc: "Audit trails, secure user roles, and export options"
  }, {
    id: 3,
    title: "Local Qualification & Service",
    desc: "Full IQ/OQ validation protocols across Ireland and the UK"
  }];
  const quickBadges = ["Flowability & Tapped Density", "Tablet Hardness & Thickness", "Disintegration & Friability", "Granule Characterisation"];
  return <section id="productfamilyhero" data-nav="light" className="relative overflow-hidden bg-background pt-8 pb-16 md:pt-12 md:pb-24 lg:pt-16 lg:pb-28">
      {/* Subtle geometric background grid */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(#00000008_1px,transparent_1px)] [background-size:24px_24px] dark:bg-[radial-gradient(#ffffff0a_1px,transparent_1px)]" aria-hidden="true" />

      {/* Decorative ambient glow */}
      <div className="pointer-events-none absolute -top-40 right-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute top-1/2 left-0 h-80 w-80 rounded-full bg-chart-3/10 blur-3xl" aria-hidden="true" />

      <div className="page-container relative z-10">
        {/* Breadcrumb Navigation */}
        <div className="mb-6">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/HomePage">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/ProductsPage">Products</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Powder & Tablet Testing</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Main Hero Copy */}
          <div className="lg:col-span-7">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="flex flex-col gap-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                  <Sparkles className="size-3.5 text-primary" />
                  Regulated Laboratory Solutions
                </Badge>
                <span className="text-xs font-medium text-muted-foreground">
                  EP / USP Standards
                </span>
              </div>

              <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl/tight">
                Powder & Tablet Testing Instruments
              </h1>

              <p className="text-base text-muted-foreground sm:text-lg">
                Precise, pharmacopoeia-compliant physical testing instruments engineered for
                routine quality control, formulation development, and solid dose characterisation
                in regulated pharmaceutical and chemical manufacturing.
              </p>

              {/* Functional Pills */}
              <div className="flex flex-wrap gap-2 pt-1">
                {quickBadges.map((badge, idx) => <span key={badge} data-index={idx} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary/50 px-2.5 py-1 text-xs font-medium text-secondary-foreground">
                    <CheckCircle2 className="size-3 text-primary" />
                    {badge}
                  </span>)}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3 pt-3">
                <Button asChild size="lg" className="shadow-sm font-medium">
                  <Link to="/ContactPage">
                    Request Instrument Quote
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg">
                  <Link to="/ServicesPage">
                    <SlidersHorizontal className="size-4" />
                    Validation & IQ/OQ Support
                  </Link>
                </Button>
              </div>

              {/* Trust & Guarantee Indicators */}
              <div className="mt-4 grid grid-cols-1 gap-3 border-t border-border pt-6 sm:grid-cols-3">
                {keySpecifications.map((spec, specIdx) => <div key={spec.id} data-index={specIdx} className="flex flex-col gap-1 rounded-lg bg-secondary/30 p-3 border border-border/50">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                      <ShieldCheck className="size-3.5 text-primary" />
                      <span>{spec.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {spec.desc}
                    </p>
                  </div>)}
              </div>
            </motion.div>
          </div>

          {/* Hero Visual Presentation */}
          <div className="lg:col-span-5">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            scale: 1
          } : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.1,
            ease: "easeOut"
          }} className="relative mx-auto max-w-lg lg:max-w-none">
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-3 shadow-xl">
                <div className="relative aspect-4/3 w-full overflow-hidden rounded-xl bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_hero.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_hero.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_hero.webp-wvc-sizes" alt="Precision laboratory powder and tablet testing instrumentation" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent" />

                  {/* Floating specification overlay badge */}
                  <div className="absolute bottom-4 left-4 right-4 rounded-lg bg-background/95 p-3.5 backdrop-blur-xs border border-border shadow-md">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                          <FileCheck2 className="size-4" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground">
                            Professionally Calibrated
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            Complete traceable qualification packs
                          </p>
                        </div>
                      </div>
                      <Button asChild size="sm" variant="secondary" className="text-xs font-medium">
                        <Link to="/ContactPage">
                          <PhoneCall className="size-3.5" />
                          Consult
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative accent card behind image */}
              <div className="pointer-events-none absolute -bottom-3 -right-3 -z-10 h-full w-full rounded-2xl border border-primary/20 bg-primary/5" aria-hidden="true" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}